import { useLayoutEffect, useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib';
import type { BeadState } from '../../model';

interface StageProgressProps {
  states: BeadState[];
  current: number;
  total: number;
  /** Ответ дан — текущая засечка сразу показывает исход, а не остаётся оранжевой. */
  answered: boolean;
}

/** Полоска прогресса в шапке сцены: одна засечка на карточку плюс счётчик
 *  «03 / 20». Текущая засечка шире остальных и всегда держится в зоне
 *  видимости — при 40 карточках ряд шире экрана, поэтому он прокручивается
 *  (мышью — перетаскиванием, пальцем — нативно). */
export function StageProgress({ states, current, total, answered }: StageProgressProps) {
  const { t } = useTranslation();
  const wrapRef = useRef<HTMLDivElement>(null);
  const markRef = useRef<HTMLSpanElement>(null);
  const didCenter = useRef(false);
  const drag = useRef<{ x: number; left: number; id: number } | null>(null);

  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const mark = markRef.current;
    if (!wrap || !mark) return;
    // Не перебиваем ручную прокрутку: пока тянут, не центрируем.
    if (drag.current) return;
    // Считаем от САМОГО скроллера через rect'ы, а не через offsetLeft: offsetParent
    // у засечки — `.lx-stage` (она position:fixed), поэтому offsetLeft включал бы
    // отступ всей шапки и уводил прокрутку в самый конец на каждом пересчёте.
    const wrapRect = wrap.getBoundingClientRect();
    const markRect = mark.getBoundingClientRect();
    const delta = markRect.left - wrapRect.left - (wrap.clientWidth - markRect.width) / 2;
    if (Math.abs(delta) < 1) return;
    wrap.scrollTo({
      left: wrap.scrollLeft + delta,
      behavior: didCenter.current ? 'smooth' : 'auto',
    });
    didCenter.current = true;
  }, [current, total]);

  const onPointerDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    if (ev.pointerType !== 'mouse' || ev.button !== 0) return;
    const wrap = wrapRef.current;
    if (!wrap) return;
    // preventDefault гасит выделение текста: иначе Chrome тянет выделение за
    // курсором и САМ доскроливает ближайший скроллер — полоску дёргало рывками
    // навстречу нашему scrollLeft.
    ev.preventDefault();
    // Плавная прокрутка от авто-центрирования могла остаться в полёте и драться
    // с ручным scrollLeft — на время перетаскивания отключаем её совсем.
    wrap.style.scrollBehavior = 'auto';
    drag.current = { x: ev.clientX, left: wrap.scrollLeft, id: ev.pointerId };
    wrap.setPointerCapture(ev.pointerId);
    wrap.classList.add('is-dragging');
  };
  const onPointerMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    if (!wrap || drag.current?.id !== ev.pointerId) return;
    ev.preventDefault();
    wrap.scrollLeft = drag.current.left - (ev.clientX - drag.current.x);
  };
  const endDrag = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const wrap = wrapRef.current;
    if (!wrap || drag.current?.id !== ev.pointerId) return;
    drag.current = null;
    wrap.style.scrollBehavior = '';
    wrap.classList.remove('is-dragging');
    if (wrap.hasPointerCapture(ev.pointerId)) wrap.releasePointerCapture(ev.pointerId);
  };

  return (
    <div className="st-progress" title={t('session.progressTitle', { current: current + 1, total })}>
      <div
        ref={wrapRef}
        className="st-ticks-wrap"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onLostPointerCapture={endDrag}
        onDragStart={(ev) => ev.preventDefault()}
      >
        <div className="st-ticks">
          {Array.from({ length: total }).map((_, i) => {
            const isCurrent = i === current;
            const state = states[i] ?? 'pending';
            return (
              <span
                key={i}
                ref={isCurrent ? markRef : undefined}
                className={cn(
                  'st-tick',
                  state,
                  // Пока ждём ответ — засечка оранжевая и широкая; как ответили,
                  // она тут же принимает цвет исхода, оставаясь широкой.
                  isCurrent && (answered ? 'is-wide' : 'is-current'),
                )}
              />
            );
          })}
        </div>
      </div>
      <span className="st-count">
        {String(Math.min(current + 1, total)).padStart(2, '0')}
        <i> / {total}</i>
      </span>
    </div>
  );
}
